import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getPaymentLink } from '../../utils/paymentLinks';

type PaymentState = 'default' | 'success' | 'cancel';

export default function Niveau1Page() {
  const [paymentState, setPaymentState] = useState<PaymentState>('default');
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  const checkPaymentParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'success' && sessionId) {
      setPaymentState('success');
    } else if (payment === 'cancel') {
      setPaymentState('cancel');
    } else {
      setPaymentState('default');
    }
    if (payment) {
      window.history.replaceState({}, '', '/niveau-1');
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    checkPaymentParams();
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) checkPaymentParams();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      clearTimeout(t);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [location.search, checkPaymentParams]);

  const handlePayment = () => {
    window.location.href = getPaymentLink('niveau1');
  };

  const isPaid = paymentState === 'success';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed inset-0 z-10"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.82) 100%)' }}
      />

      <div className="sticky top-0 z-50 bg-black/95 border-b border-gray-800 py-3 px-4">
        <div className="text-center">
          <div className="text-xs text-gray-500 font-light tracking-widest mb-1.5">ÉTAPE 1 SUR 6</div>
          <div className="flex gap-1.5 justify-center mb-2">
            <div className="w-14 h-3 bg-white rounded-full shadow-[0_0_8px_1px_rgba(255,255,255,0.4)]"></div>
            <div className="w-14 h-3 bg-gray-700/60 rounded-full"></div>
            <div className="w-14 h-3 bg-gray-700/60 rounded-full"></div>
            <div className="w-14 h-3 bg-gray-700/60 rounded-full"></div>
            <div className="w-14 h-3 bg-gray-700/60 rounded-full"></div>
            <div className="w-14 h-3 bg-gray-700/60 rounded-full"></div>
          </div>
          <div className="text-[11px] text-red-500 font-bold tracking-wider uppercase">⚠ STATUT DU DOSSIER : NON SÉCURISÉ</div>
          <div className="text-[9px] text-gray-400 font-light mt-1">Une seule erreur peut coûter plusieurs milliers d'euros.</div>
          <div className="text-[9px] text-gray-500 font-light mt-0.5">Système sécurisé : 1 / 6</div>
        </div>
      </div>

      <div
        className="px-4 py-8 space-y-8 relative z-20 transition-all duration-500 ease-out max-w-2xl mx-auto"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(18px)' }}
      >
        {paymentState === 'cancel' && (
          <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-4 text-center">
            <p className="text-gray-400 text-sm font-light">Paiement annulé. Votre dossier reste non sécurisé.</p>
          </div>
        )}

        {!isPaid && (
          <>
            <div className="space-y-2 pt-2">
              <h1 className="text-3xl md:text-4xl leading-tight">
                <span className="font-extrabold text-white uppercase tracking-tight">VÉRIFICATION</span>
                <span className="font-light text-white"> initiale — </span>
                <span className="font-extrabold text-white">première barrière</span>
              </h1>
              <p className="text-base text-gray-300 font-light">Identifier les erreurs avant qu'elles ne coûtent cher.</p>
            </div>

            <div className="bg-[#0f0f0f] border border-gray-800/60 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white">Ce que cette étape sécurise immédiatement</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-file-pdf-line text-emerald-400 text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-300">Un guide PDF explicatif correspondant à l'étape du système</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-compass-3-line text-emerald-400 text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-300">Une méthode d'analyse utilisée dans la décision immobilière</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-multiple-line text-emerald-400 text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-300">Une checklist d'analyse applicable à un projet immobilier</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 font-light text-center pt-2">Accès immédiat après paiement.<br />Téléchargement direct des fichiers.</p>
            </div>

            <div className="bg-[#0f0f0f] border border-gray-800/60 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white">Pourquoi cette étape est importante</h2>
              <p className="text-sm text-gray-300 font-light leading-relaxed">Chaque niveau valide un point critique du projet immobilier.<br />Le système suit une progression logique afin d'éviter les décisions incohérentes.</p>
              <div className="space-y-2 text-xs text-gray-400 font-light">
                <p className="font-medium text-gray-300 mb-2">Les étapes du système sont :</p>
                <p>1. détecter les erreurs invisibles d'un projet</p>
                <p>2. structurer la réflexion et éliminer les mauvaises options</p>
                <p>3. valider la logique du projet avant les chiffres</p>
                <p>4. comparer les modèles d'investissement</p>
                <p>5. choisir la structure fiscale et juridique</p>
                <p>6. assurer la cohérence patrimoniale globale</p>
              </div>
              <div className="bg-yellow-950/40 border border-yellow-700/60 rounded-xl p-4 mt-4">
                <p className="text-yellow-400 text-xs font-bold tracking-widest uppercase mb-2">Important</p>
                <p className="text-yellow-200 text-xs font-light leading-relaxed">Le système fonctionne comme un processus de validation progressive.<br />Chaque étape dépend de la précédente.<br /><br />Sauter une étape peut conduire à une analyse incohérente du projet.</p>
              </div>
            </div>

            <div className="bg-[#0f0f0f] border border-gray-800/60 rounded-2xl p-6 space-y-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 font-light mb-2">Sécurité du projet</p>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <span className="text-3xl font-bold text-red-400">63</span>
                  <i className="ri-arrow-right-line text-gray-600 text-xl"></i>
                  <span className="text-3xl font-bold text-emerald-400">71</span>
                </div>
              </div>
              <div className="w-full bg-gray-800/50 rounded-full h-2.5 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-emerald-500 h-full rounded-full transition-all duration-700" style={{ width: '71%' }}></div>
              </div>
              <p className="text-center text-xs text-gray-400 font-light leading-relaxed">Le projet reste exposé à des risques structurels.<br />Continuer le processus pour sécuriser totalement la décision.</p>
              <p className="text-center text-[11px] text-emerald-600 font-medium mt-1">Première validation du projet immobilier</p>
            </div>

            <div className="bg-red-900/70 border-2 border-red-600 rounded-xl p-5">
              <p className="text-red-400 text-xs font-semibold tracking-widest uppercase mb-2">⚠ Situation actuelle :</p>
              <p className="text-red-100 text-[15px] font-bold leading-snug uppercase tracking-wide">PROJET EXPOSÉ À DES ERREURS COÛTEUSES</p>
            </div>

            <div className="bg-[#0f0f0f] border border-gray-800/60 rounded-2xl p-6 space-y-5">
              <div className="bg-red-950/60 border border-red-700/60 rounded-xl p-4">
                <p className="text-red-400 text-xs font-bold tracking-widest uppercase mb-2">⚠ Situation actuelle</p>
                <p className="text-red-200 text-sm font-medium leading-snug">Le dossier reste exposé à des risques structurels non validés.</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs font-light mb-3">Sans validation de cette étape, certaines décisions peuvent entraîner :</p>
                <ul className="space-y-2">
                  {['une rentabilité fausse', 'un montage fiscal incorrect', 'un refus bancaire', 'une perte de rentabilité du projet'].map((risk) => (
                    <li key={risk} className="flex items-center gap-2 text-xs text-gray-400">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-gray-800/60 pt-4 space-y-2">
                <p className="text-xs text-gray-500 font-light text-center">Niveau de sécurité du projet</p>
                <p className="text-center text-sm font-bold text-red-400">63 / 100 — dossier non sécurisé</p>
                <p className="text-center text-[11px] text-gray-500 font-light">Le projet reste vulnérable tant que cette étape n'est pas validée.</p>
              </div>
            </div>

            <div className="bg-[#0f0f0f] border border-emerald-800/40 rounded-xl p-5">
              <p className="text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-2">Niveau 1 — Objectif</p>
              <p className="text-gray-200 text-sm font-light leading-relaxed">Détecter les erreurs invisibles dans un projet immobilier avant toute analyse approfondie.</p>
            </div>

            <div className="pt-2">
              <button
                onClick={handlePayment}
                className="w-full max-w-xl mx-auto block bg-white text-black rounded-3xl py-7 text-2xl md:text-3xl font-bold transition-all duration-200 shadow-[0_10px_36px_rgba(255,255,255,0.25),0_0_16px_rgba(255,255,255,0.15)] hover:shadow-[0_12px_42px_rgba(255,255,255,0.32),0_0_20px_rgba(255,255,255,0.2)] hover:bg-gray-50 whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.97]"
              >
                👉 Identifier les erreurs du projet — 29€
              </button>
              <div className="mt-3 text-center space-y-1">
                <p className="text-xs text-gray-400 font-medium">Paiement sécurisé — téléchargement immédiat</p>
                <p className="text-[10px] text-gray-500 font-light">Accès instantané au fichier d'analyse.</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-400 font-medium">Téléchargement immédiat après paiement sécurisé</p>
            <p className="text-center text-[10px] text-gray-500 font-light mt-2">Sans cette étape, les erreurs invisibles restent actives.</p>
          </>
        )}

        {isPaid && (
          <div className="space-y-6">
            <div className="bg-emerald-950/60 border-2 border-emerald-600 rounded-lg p-5 text-center">
              <p className="text-emerald-400 text-sm font-semibold tracking-wide uppercase mb-1">✓ Étape 1 validée</p>
              <p className="text-emerald-200 text-xs font-light">Votre dossier n'est pas encore totalement sécurisé.</p>
            </div>
            <Link
              to="/niveau-2"
              className="block w-full max-w-xl mx-auto bg-white text-black rounded-3xl py-7 text-2xl md:text-3xl font-bold transition-all duration-200 shadow-[0_8px_28px_rgba(255,255,255,0.18),0_0_12px_rgba(255,255,255,0.1)] hover:shadow-[0_10px_36px_rgba(255,255,255,0.25)] hover:bg-gray-50 whitespace-nowrap cursor-pointer text-center hover:scale-[1.02] active:scale-[0.97]"
            >
              Continuer vers le niveau suivant
            </Link>
            <a href="#" className="block w-full bg-gray-800 text-white rounded-xl py-3 text-sm font-light hover:bg-gray-700 transition-all duration-300 text-center cursor-pointer">
              📥 Télécharger le fichier acheté
            </a>
          </div>
        )}

        <div className="border-t border-gray-800/40 pt-4 mt-8">
          <p className="text-center text-[10px] text-gray-600 font-light">Système complet : 6 étapes nécessaires.</p>
        </div>
      </div>

      <div className="relative z-20 border-t border-gray-800/40 mt-8 py-3 px-4">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1 text-[10px] text-gray-600 font-light"><i className="ri-lock-line text-gray-600 text-xs"></i>Paiement 100% sécurisé</span>
          <span className="text-gray-700 text-[10px]">·</span>
          <span className="flex items-center gap-1 text-[10px] text-gray-600 font-light"><i className="ri-flashlight-line text-gray-600 text-xs"></i>Accès immédiat après paiement</span>
          <span className="text-gray-700 text-[10px]">·</span>
          <span className="flex items-center gap-1 text-[10px] text-gray-600 font-light"><i className="ri-shield-check-line text-gray-600 text-xs"></i>Données protégées</span>
          <span className="text-gray-700 text-[10px]">·</span>
          <span className="flex items-center gap-1 text-[10px] text-gray-600 font-light"><i className="ri-file-text-line text-gray-600 text-xs"></i>Facture disponible</span>
        </div>
      </div>
    </div>
  );
}
